import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import api, { formatApiError } from "@/lib/api";
import PasswordStrengthMeter from "@/components/PasswordStrengthMeter";

export default function ResetPassword() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const token = useMemo(() => params.get("token") || "", [params]);

    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const invalidLink = !token || token.length < 20;
    const mismatch = confirm.length > 0 && password !== confirm;
    const tooShort = password.length > 0 && password.length < 8;
    const canSubmit = !invalidLink && password.length >= 8 && password === confirm && !loading;

    const submit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setLoading(true);
        try {
            await api.post("/auth/reset-password", { token, password });
            setDone(true);
            toast.success("Password updated. Please log in.");
            setTimeout(() => navigate("/login"), 1600);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    if (invalidLink) {
        return (
            <div className="min-h-[85vh] bg-background py-16">
                <div className="container-page max-w-md">
                    <div className="card-soft p-8" data-testid="reset-password-invalid">
                        <h1 className="font-heading font-semibold text-2xl text-foreground">Reset link missing</h1>
                        <p className="mt-2 text-sm text-muted-foreground">
                            This URL doesn&apos;t include a valid reset token. Request a fresh link from the forgot-password page.
                        </p>
                        <Link
                            to="/forgot-password"
                            data-testid="reset-request-new-link"
                            className="btn-green mt-6 inline-flex w-full items-center justify-center"
                        >
                            Request a new link
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-[85vh] bg-background py-16">
            <div className="container-page max-w-md">
                <div className="card-soft p-8" data-testid="reset-password-card">
                    <h1 className="font-heading font-semibold text-2xl text-foreground">Set a new password</h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Choose a strong password. We&apos;ll sign you out of every device once it&apos;s updated.
                    </p>

                    {done ? (
                        <div
                            data-testid="reset-password-success"
                            className="mt-6 rounded-2xl border border-emerald-500/30 bg-accent p-5 text-sm text-accent-foreground dark:text-emerald-300"
                        >
                            <ShieldCheck className="mb-2" size={22} />
                            <p className="font-semibold text-foreground dark:text-emerald-100">Password updated.</p>
                            <p className="mt-1 text-muted-foreground dark:text-emerald-200/80">
                                Redirecting you to log in…
                            </p>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="mt-6 space-y-4">
                            <div>
                                <Label htmlFor="reset-password">
                                    New password <span className="text-muted-foreground font-normal">(min 8 characters)</span>
                                </Label>
                                <Input
                                    id="reset-password"
                                    type="password"
                                    required
                                    minLength={8}
                                    autoComplete="new-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    data-testid="reset-password-input"
                                    className="mt-2"
                                />
                                {tooShort && (
                                    <p className="mt-1 text-xs text-destructive" data-testid="reset-password-too-short">
                                        Must be at least 8 characters.
                                    </p>
                                )}
                                <PasswordStrengthMeter password={password} userInputs={["simonara"]} testId="reset-password-strength" />
                            </div>
                            <div>
                                <Label htmlFor="reset-confirm">Confirm password</Label>
                                <Input
                                    id="reset-confirm"
                                    type="password"
                                    required
                                    autoComplete="new-password"
                                    value={confirm}
                                    onChange={(e) => setConfirm(e.target.value)}
                                    data-testid="reset-confirm-input"
                                    className="mt-2"
                                />
                                {mismatch && (
                                    <p className="mt-1 text-xs text-destructive" data-testid="reset-password-mismatch">
                                        Passwords don&apos;t match.
                                    </p>
                                )}
                            </div>
                            <button
                                type="submit"
                                disabled={!canSubmit}
                                data-testid="reset-submit-btn"
                                className="btn-green w-full disabled:opacity-60"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 size={18} className="mr-2 animate-spin" /> Updating…
                                    </>
                                ) : (
                                    "Update password"
                                )}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
